import { useState, useRef } from 'react';
import { toast } from 'react-hot-toast';

/**
 * Image upload and S3 related logic hook
 */
export const useImageUpload = () => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [s3ImageUrl, setS3ImageUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const currentFileRef = useRef(null);
  const fileInputRef = useRef(null);

  const processFile = async (file, { onReset } = {}) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files can be uploaded');
      return;
    }

    setUploadedImage(file);
    setS3ImageUrl(null);
    onReset?.();

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    // S3에 직접 업로드 (presigned URL 방식)
    setIsUploading(true);
    currentFileRef.current = file;

    try {
      const WS_URL = import.meta.env.VITE_LABS_WS_URL || 'wss://uknjkyctd3.execute-api.us-east-1.amazonaws.com/prod';
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        ws.send(JSON.stringify({
          action: 'getUploadUrl',
          fileName: file.name,
          contentType: file.type,
        }));
      };

      ws.onmessage = async (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'uploadUrl') {
            const uploadResponse = await fetch(message.uploadUrl, {
              method: 'PUT',
              body: file,
              headers: { 'Content-Type': file.type },
            });
            if (uploadResponse.ok) {
              setS3ImageUrl(message.imageUrl);
              setIsUploading(false);
              toast.success('Image uploaded successfully');
            } else {
              throw new Error('S3 upload failed');
            }
            ws.close();
          } else if (message.type === 'error') {
            throw new Error(message.error);
          }
        } catch (e) {
          setIsUploading(false);
          toast.error('Upload failed: ' + e.message);
          ws.close();
        }
      };

      ws.onerror = () => {
        setIsUploading(false);
        toast.error('Server connection failed');
      };
    } catch (error) {
      setIsUploading(false);
      toast.error('Upload failed');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e) => e.preventDefault();

  const resetUpload = () => {
    setPreviewUrl(null);
    setUploadedImage(null);
    setS3ImageUrl(null);
  };

  return {
    uploadedImage,
    previewUrl, setPreviewUrl,
    s3ImageUrl, setS3ImageUrl,
    isUploading,
    fileInputRef,
    processFile,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    resetUpload,
  };
};
